import { useEffect, useRef } from 'react';

import type {
	DependencyHealthAttributes,
	DependencyOutcome,
} from '../dependencyHealthAnalytics.ts';

import { useDependencyHealthAnalytics } from './useDependencyHealthAnalytics.ts';
import { useQueryLoadingDuration } from './useQueryLoadingDuration.ts';

export type DependencyRead =
	| { status: 'idle' | 'loading' }
	| { status: 'settled'; outcome: DependencyOutcome; result: unknown; error?: unknown };

export type DependencyReadHealthOptions = Pick<
	DependencyHealthAttributes,
	'dependency' | 'operation'
> & {
	identity: string;
	read: DependencyRead;
};

type ReportedRead = {
	identity: string;
	result: unknown;
	error: unknown;
	outcome: DependencyOutcome;
};

export const useDependencyReadHealth = ({
	dependency,
	operation,
	identity,
	read,
}: DependencyReadHealthOptions): void => {
	const report = useDependencyHealthAnalytics();
	const lastReported = useRef<ReportedRead | null>(null);
	const settled = read.status === 'settled' ? read : undefined;
	const result = settled?.result;
	const error = settled?.error;
	const outcome = settled?.outcome;
	const getDuration = useQueryLoadingDuration({
		enabled: read.status !== 'idle',
		identity,
		loading: read.status === 'loading',
		result,
		error,
	});

	useEffect(() => {
		if (read.status !== 'settled') {
			lastReported.current = null;
			return;
		}
		if (outcome === undefined) {
			return;
		}
		const previous = lastReported.current;
		if (
			previous?.identity === identity &&
			previous.result === result &&
			previous.error === error &&
			previous.outcome === outcome
		) {
			return;
		}
		lastReported.current = { identity, result, error, outcome };
		const { queryLoadingDurationMs: durationMs } = getDuration(result, error);
		report({ dependency, operation, outcome, ...(durationMs === undefined ? {} : { durationMs }) });
	}, [dependency, operation, identity, read.status, result, error, outcome, getDuration, report]);
};
